/**
 * 🎨 `motionStyle` — M 카드 그리기 스타일(2026-09-28 · 모션 스타일 재편 1단계 · 정본 지도 `docs/01-plan/features/motion-style-pack.md`).
 *   · 미지정 / `"classic"` = 종전 카드 **바이트 그대로**(아래 본문은 1비트도 안 바뀌었다 — 자취 계약 테스트가 잠근다).
 *   · `"kit"` = 부품 상자(`lib/motion-kit`) «모션그래픽» — 글자 무손상·비용 0·결정론. 부품 조합은 제목·줄로 자동 선택.
 *   ★기본값 교체는 회장님 결정★ — 호출부가 명시할 때만 새 경로로 간다(«행 없으면 종전»).
 */
export type MotionCardStyle = "classic" | "kit";
export interface MotionCardPaintData {title:string;lines:string[];characterImg?:HTMLImageElement|null;motionStyle?:MotionCardStyle;/** 컷 길이(초) — `kit` 만 읽는다(ms 기본값·노출 하한 환산). 종전 경로는 무시. */durationSec?:number;}
import {titleFontCss} from "../caption-fonts";
import {drawMotionKitCard} from "../motion-kit/card";
import {wrapByMeasure,motionCardLayout,motionCardLineSizePx,motionCardTitleSizePx} from "../motion-card-layout";

export function drawDataScene(
  ctx: CanvasRenderingContext2D,
  motion: MotionCardPaintData,
  W: number,
  H: number,
  t01: number,
  activeTitleFontId = "default",
) {
  // 🧰 «모션그래픽» 분기 — `kit` 일 때만. 아래 종전 경로는 손대지 않는다(정본 지도 ADR 09-28).
  if (motion.motionStyle === "kit") {
    drawMotionKitCard(ctx, motion, W, H, t01, activeTitleFontId);
    return;
  }
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, "#16213e");
  grad.addColorStop(1, "#0b1020");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#3b82f6";
  ctx.fillRect(0, 0, W, Math.max(3, Math.round(H * 0.012)));

  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  /**
   * 📐 **좌표·줄바꿈·y 누적은 ★순수 모듈★ 이 계산한다**(`motionCardLayout` · 2026-08-24 R1 추출).
   *   ★추출이지 개정이 아니다★ — 산식은 1비트도 안 바뀌었고 이 함수는 그 결과를 순서대로 그리기만
   *   한다. 뺀 이유는 하나다: **지목 글자 검증이 "캔버스 안에 실제로 그려지는 조각"을 알아야 하는데**
   *   그 판정을 렌더러 안에 두면 검증이 산식을 사본으로 베끼게 된다(그 사본이 갈리는 날 화면 밖
   *   글자를 지목하게 되고, 그건 곧 모델이 글자를 지어내는 사고다 — 모듈 헤더 참조).
   * ★폰트는 측정 콜백이 세운다★ — `ctx.font` 파싱을 매번 하지 않도록 직전 값을 기억한다(성능만).
   */
  const titleSize = motionCardTitleSizePx(W); // 🔠 산식 + 하한 SSOT(motion-card-layout 헤더·회귀 0)
  const lineSize = motionCardLineSizePx(W);
  const titleFont = titleFontCss(activeTitleFontId, titleSize, 800); // 🔤 제목 폰트 — default=기존 문자열 그대로
  const lineFont = `600 ${lineSize}px "Noto Sans KR", "Apple SD Gothic Neo", sans-serif`;
  let activeFont = "";
  const measureWith = (font: string) => (text: string) => {
    if (activeFont !== font) { ctx.font = font; activeFont = font; }
    return ctx.measureText(text).width;
  };
  const layout = motionCardLayout({
    title: motion.title || "",
    lines: motion.lines,
    W,
    H,
    t01,
    hasCharacterImage: !!motion.characterImg && motion.characterImg.width > 0,
    measureTitle: measureWith(titleFont),
    measureLine: measureWith(lineFont),
  });

  ctx.font = titleFont;
  ctx.globalAlpha = Math.min(1, t01 / 0.12);
  ctx.fillStyle = "#7dd3fc";
  for (const p of layout.pieces) {
    if (p.kind === "title") ctx.fillText(p.text, p.x, p.y);
  }
  ctx.globalAlpha = 1;

  ctx.font = lineFont;
  // ★점(●)이 그 줄의 존재를 나타낸다★ — 줄바꿈 조각이 0개인 줄에도 종전엔 점이 찍혔으므로,
  //   점 목록을 순회하고 그 안에서 같은 원문의 조각을 그린다(종전 forEach 와 같은 순서·같은 alpha).
  for (const dot of layout.dots) {
    ctx.globalAlpha = dot.appear;
    ctx.fillStyle = "#fbbf24";
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, dot.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#e5e7eb";
    for (const p of layout.pieces) {
      if (p.kind === "line" && p.sourceIndex === dot.sourceIndex) ctx.fillText(p.text, p.x, p.y);
    }
    ctx.globalAlpha = 1;
  }
  const pad = layout.pad;

  const img = motion.characterImg;
  if (img && img.width > 0) {
    const cw = W * 0.24;
    const ch = cw * (img.height / img.width || 1.3);
    ctx.globalAlpha = Math.min(1, t01 / 0.18);
    ctx.drawImage(img, W - cw - pad * 0.4, H - ch - pad * 0.3, cw, ch);
    ctx.globalAlpha = 1;
  }
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  overlay: {headline:string},
  W: number,
  H: number,
  t01: number,
) {
  const headline = (overlay.headline || "").trim();
  if (!headline) return;
  const appear = Math.min(1, t01 / 0.15);
  const fs = Math.round(W * 0.055);
  ctx.font = `800 ${fs}px "Noto Sans KR", "Apple SD Gothic Neo", sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const lines = wrapByMeasure(text=>ctx.measureText(text).width,headline,W*0.62);
  const lineH = fs * 1.2;
  const blockH = lines.length * lineH;
  const padX = Math.round(W * 0.045);
  const topY = Math.round(H * 0.1);
  const slideX = padX + (1 - appear) * (W * 0.03);
  const widest = Math.min(W * 0.62, Math.max(...lines.map((l) => ctx.measureText(l).width)));

  ctx.globalAlpha = appear;
  ctx.fillStyle = "rgba(245, 158, 11, 0.93)";
  roundRect(ctx, slideX - W * 0.012, topY - fs * 0.28, widest + W * 0.05, blockH + fs * 0.55, fs * 0.22);
  ctx.fill();
  ctx.fillStyle = "#1a1206";
  let y = topY;
  for (const l of lines) {
    ctx.fillText(l, slideX, y);
    y += lineH;
  }
  ctx.globalAlpha = 1;
}
